import React, { useState } from "react";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Plus, Trash2, CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { CustomRate } from "@/types/earnings";
import { useFilteredRequests } from "@/hooks/useFilteredRequests";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { HoursWorked } from "@/types/nanny";

interface EarningRate {
  code: string;
  displayName: string;
}

interface HoursSectionProps {
  hoursWorked: HoursWorked[];
  earningRates: CustomRate[];
  submissionId: number;
  isEditable: boolean;
  onUpdate?: (hours: HoursWorked[]) => void;
}

const hoursSchema = z.object({
  earningCode: z.string().min(1, "Please select an earning type"),
  hours: z
    .number()
    .min(0.5, "Hours must be at least 0.5")
    .max(24, "Hours cannot exceed 24 per day"),
  workDate: z.date(),
});

type FormValues = z.infer<typeof hoursSchema>;

const formatToISODate = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

const HoursSection: React.FC<HoursSectionProps> = ({
  hoursWorked,
  earningRates,
  submissionId,
  isEditable,
  onUpdate,
}) => {
  const { filteredItems, showDeleted, setShowDeleted } =
    useFilteredRequests(hoursWorked);
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(hoursSchema),
    defaultValues: {
      hours: undefined,
      workDate: new Date(),
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      // Convert the date to YYYY-MM-DD format before sending
      const formattedValues = {
        ...values,
        workDate: formatToISODate(values.workDate),
      };

      const response = await ApiService.request<any>(
        EndpointKeys.CREATE_HOURS,
        {
          method: "POST",
          urlParams: { submissionId },
          body: formattedValues,
        },
      );

      // Update parent state with new hours entry
      const newHoursEntry = response.data;
      if (onUpdate) {
        onUpdate([...hoursWorked, newHoursEntry]);
      }

      toast({
        title: "Success",
        description: "Hours added successfully",
      });

      form.reset();
      setIsDialogOpen(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to add hours",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await ApiService.request(EndpointKeys.DELETE_HOURS, {
        urlParams: { submissionId, id },
        method: "DELETE",
      });

      // Update parent state by filtering out deleted hours entry
      const updatedHours = hoursWorked.filter((entry) => entry.id !== id);
      if (onUpdate) {
        onUpdate(updatedHours);
      }

      toast({
        title: "Success",
        description: "Hours deleted successfully",
      });
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to delete hours",
        variant: "destructive",
      });
    }
  };
  return (
    <div>
      {isEditable && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Hours Worked</DialogTitle>
              <DialogDescription>
                Record your working hours for a specific date and type of work
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="workDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              data-testid="hours-date-button"
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 pointer-events-auto"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={field.value}
                            defaultMonth={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="earningCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type of Work</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="earning-code-select">
                            <SelectValue placeholder="Select type of work" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {earningRates.map((rate) => (
                            <SelectItem
                              key={rate.customRateVersion}
                              value={rate.customRateVersion.toString()}
                            >
                              {rate.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hours</FormLabel>
                      <FormControl>
                        <Input
                          data-testid="hours-input"
                          type="number"
                          step="0.5"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Enter hours worked in 0.5 hour increments
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button data-testid="save-hours-button" type="submit" className="w-full">
                  Add Hours
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex justify-between items-center mb-4">
        {isEditable && (
          <Button data-testid="add-hours-button" onClick={() => setIsDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Hours
          </Button>
        )}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-deleted"
            checked={showDeleted}
            onCheckedChange={setShowDeleted}
          />
          <Label htmlFor="show-deleted">Show deleted hours</Label>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Status</TableHead>
              {isEditable && (
                <TableHead className="w-[100px]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isEditable ? 5 : 4}
                  className="text-center text-muted-foreground"
                >
                  No hours recorded
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    {format(new Date(entry.work_date), "PPP")}
                  </TableCell>
                  <TableCell>{entry.earning_name}</TableCell>
                  <TableCell>{entry.hours}</TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "rounded-full px-2 py-1 text-xs font-medium",
                        {
                          "bg-green-100 text-green-800":
                            entry.status === "APPROVED",
                          "bg-yellow-100 text-yellow-800":
                            entry.status === "PENDING",
                          "bg-red-100 text-red-800":
                            entry.status === "REJECTED",
                          "bg-gray-100 text-gray-800":
                            entry.status === "DELETED",
                        },
                      )}
                    >
                      {entry.status}
                    </span>
                  </TableCell>
                  {isEditable && (
                    <TableCell>
                      {entry.status !== "DELETED" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(entry.id)}
                          className="hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default HoursSection;
